import React, { useState, useEffect, useRef } from "react";
import "./BenefitsAdmin.css";
import { useNavigate } from 'react-router-dom';
import BenefitsHero from "./BenefitsHero";
import BenefitsWiseCode from "./BenefitsWiseCode";


const VideoPopup = ({ isOpen, onClose, videoUrl }) => {
  const videoRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      if (videoRef.current) {
        videoRef.current.currentTime = 0;
      }
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="benefits-video-popup-overlay" onClick={onClose}>
      <div className="benefits-video-popup-content" onClick={(e) => e.stopPropagation()}>
        <button className="benefits-video-popup-close" onClick={onClose}>
          ×
        </button>
        <div className="benefits-video-container">
          <video ref={videoRef} controls autoPlay className="benefits-demo-video">
            <source src={videoUrl} type="video/mp4" />
            Your browser does not support the video tag.
          </video>
        </div>
        <div className="benefits-video-popup-info">
          <h3>Benefits Administration Platform</h3>
          <p>See how our platform simplifies benefits management and enhances employee experience</p>
        </div>
      </div>
    </div>
  );
};

const BenefitsAdmin = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [isVideoPopupOpen, setIsVideoPopupOpen] = useState(false);
  const [activeFeature, setActiveFeature] = useState(0);
  const [scrollProgress, setScrollProgress] = useState(0);
  const [activeTab, setActiveTab] = useState("enrollment");
  const navigate = useNavigate();
    const handleRecruit = () => navigate('/access-recruit');
 const videoRef = useRef(null);
  const [scrollY, setScrollY] = useState(0);

  useEffect(() => {
    setIsVisible(true);
    
    // Auto rotate features
    const interval = setInterval(() => {
      setActiveFeature(prev => (prev + 1) % 6);
    }, 4000);
    
    // Scroll progress
    const handleScroll = () => {
      const scrollTop = window.pageYOffset;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      const progress = (scrollTop / docHeight) * 100;
      setScrollProgress(progress);
    };

    window.addEventListener('scroll', handleScroll);
    
    return () => {
      clearInterval(interval);
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const demoVideoUrl = "https://assets.mixkit.co/videos/preview/mixkit-woman-analyzing-medical-results-on-a-computer-44538-large.mp4";

  const openVideoPopup = () => {
    setIsVideoPopupOpen(true);
  };

  const closeVideoPopup = () => {
    setIsVideoPopupOpen(false);
  };

  const features = [
    {
      icon: "🏥",
      title: "Healthcare Management",
      description: "Comprehensive medical, dental, and vision benefits administration with carrier integrations"
    },
    {
      icon: "💰",
      title: "Retirement Plans",
      description: "401(k), 403(b), and pension plan management with compliance tracking and reporting"
    },
    {
      icon: "📊",
      title: "Benefits Analytics",
      description: "Real-time insights on benefits utilization, costs, and employee engagement metrics"
    },
    {
      icon: "🔄",
      title: "Open Enrollment",
      description: "Streamlined enrollment processes with guided workflows and decision support tools"
    },
    {
      icon: "⚡",
      title: "COBRA & Compliance",
      description: "Automated COBRA administration and compliance management for regulatory requirements"
    },
    {
      icon: "🤝",
      title: "Employee Self-Service",
      description: "Intuitive portal for employees to manage benefits, view plans, and make changes"
    }
  ];

 const benefitsStages = [
  {
    phase: "Assessment",
    icon: "📋",
    time: "1-2 days",
    title: "Needs Analysis & Planning",
    steps: [
      "Current benefits audit",
      "Employee needs assessment",
      "Budget analysis & planning",
      "Compliance requirements review"
    ]
  },
  {
    phase: "Design",
    icon: "🎨",
    time: "3-5 days",
    title: "Benefits Package Design",
    steps: [
      "Custom plan configuration",
      "Carrier selection & negotiation",
      "Cost modeling & optimization",
      "Employee communication planning"
    ]
  },
  {
    phase: "Implementation",
    icon: "⚡",
    time: "2-4 weeks",
    title: "System Implementation & Enrollment",
    steps: [
      "Platform configuration",
      "Employee data migration",
      "Automated enrollment setup",
      "Integration with payroll & HR systems"
    ]
  },
  {
    phase: "Management",
    icon: "🔄",
    time: "Ongoing",
    title: "Continuous Optimization & Support",
    steps: [
      "Real-time utilization monitoring",
      "Cost optimization recommendations",
      "Compliance updates & alerts",
      "Employee satisfaction tracking"
    ]
  }
];

const benefitsMetrics = [
  { name: "Health Insurance", progress: 92 },
  { name: "Retirement Plans", progress: 78 },
  { name: "Dental & Vision", progress: 85 },
  { name: "Wellness Programs", progress: 67 }
];

const benefitsIntegrations = [
  "UnitedHealthcare", "Aetna", "Cigna", "Blue Cross", 
  "Kaiser", "Humana", "ADP", "Paychex", 
  "Workday", "BambooHR", "Fidelity", "Vanguard"
];

const benefitsTabs = [
  { id: 'enrollment', label: 'Enrollment' },
  { id: 'management', label: 'Management' },
  { id: 'analytics', label: 'Analytics' },
  { id: 'compliance', label: 'Compliance' }
];

const handleAccessBenefits = () => {
  // Handle benefits management access
  window.open('/benefits-platform', '_blank');
  console.log("Access benefits management");
};

const openBenefitsVideoPopup = () => {
  // Open benefits management video popup
  window.open('/benefits-demo', '_blank');
  console.log("Open benefits management video");
};

const handleExploreBenefits = () => {
  // Handle explore benefits integrations
  window.open('/benefits-integrations', '_blank');
  console.log("Explore benefits integrations");
};

  const tabs = [
    {
      id: "enrollment",
      label: "ENROLLMENT MANAGEMENT",
      content: {
        icon: "🔄",
        title: "Streamlined Benefits Enrollment",
        description: "Simplify open enrollment with guided workflows, decision support tools, and automated eligibility verification for seamless benefits selection.",
        features: ["GUIDED ENROLLMENT", "ELIGIBILITY VERIFICATION", "DEPENDENT MANAGEMENT", "AUTO-ENROLLMENT"],
        secondary: {
          icon: "📱",
          title: "Mobile Enrollment Experience",
          description: "Enable employees to enroll in benefits from any device with intuitive mobile-friendly interfaces and real-time support."
        },
        buttonText: "EXPLORE ENROLLMENT TOOLS"
      }
    },
    {
      id: "healthcare",
      label: "HEALTHCARE BENEFITS →",
      content: {
        icon: "🏥",
        title: "Comprehensive Healthcare Management",
        description: "Manage medical, dental, and vision plans with integrated carrier connections, claims tracking, and cost analysis.",
        features: ["CARRIER INTEGRATIONS", "CLAIMS TRACKING", "COST ANALYTICS", "WELLNESS PROGRAMS"],
        secondary: {
          icon: "💊",
          title: "Pharmacy & Specialty Benefits",
          description: "Integrated pharmacy benefits management with prescription drug tracking and specialty care coordination."
        },
        buttonText: "EXPLORE HEALTHCARE TOOLS"
      }
    },
    {
      id: "retirement",
      label: "RETIREMENT PLANS",
      content: {
        icon: "💰",
        title: "Retirement Benefits Administration",
        description: "Streamline 401(k), 403(b), and pension plan management with compliance tracking, contribution management, and participant reporting.",
        features: ["PLAN ADMINISTRATION", "COMPLIANCE TRACKING", "CONTRIBUTION MANAGEMENT", "PARTICIPANT REPORTING"],
        secondary: {
          icon: "📈",
          title: "Investment & Performance Analytics",
          description: "Monitor investment performance, track participant behavior, and generate comprehensive retirement readiness reports."
        },
        buttonText: "EXPLORE RETIREMENT TOOLS"
      }
    },
    {
      id: "compliance",
      label: "COMPLIANCE & REPORTING",
      content: {
        icon: "⚖️",
        title: "Regulatory Compliance Management",
        description: "Ensure compliance with ACA, ERISA, HIPAA, and other regulations with automated reporting and audit-ready documentation.",
        features: ["ACA REPORTING", "ERISA COMPLIANCE", "HIPAA SECURITY", "AUDIT PREPARATION"],
        secondary: {
          icon: "📋",
          title: "Compliance Analytics & Alerts",
          description: "Proactive compliance monitoring with automated alerts for regulatory changes and upcoming filing deadlines."
        },
        buttonText: "EXPLORE COMPLIANCE TOOLS"
      }
    }
  ];

  const activeTabContent = tabs.find(tab => tab.id === activeTab)?.content;

  return (
    <div className="benefits-container">
      {/* Scroll Progress Bar
      <div className="benefits-scroll-progress" style={{width: `${scrollProgress}%`}}></div> */}

      {/* Video Popup */}
      <VideoPopup 
        isOpen={isVideoPopupOpen} 
        onClose={closeVideoPopup} 
        videoUrl={demoVideoUrl}
      />

      {/* Enhanced Animated Background */}
      <div className="benefits-background-animation">
        {/* <div className="benefits-floating-shape benefits-shape-1"></div>
        <div className="benefits-floating-shape benefits-shape-2"></div>
        <div className="benefits-floating-shape benefits-shape-3"></div>
        <div className="benefits-floating-shape benefits-shape-4"></div>
        <div className="benefits-floating-shape benefits-shape-5"></div> */}
        
        {/* Animated Gradient Orbs */}
        {/* <div className="benefits-gradient-orb benefits-orb-1"></div>
        <div className="benefits-gradient-orb benefits-orb-2"></div>
        <div className="benefits-gradient-orb benefits-orb-3"></div> */}
        
        {/* SVG Background Animation */}
        <svg className="benefits-bg-svg" viewBox="0 0 100 100" preserveAspectRatio="none">
          <defs>
            <linearGradient id="benefits-gradient1" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="rgba(34, 197, 94, 0.08)" />
              <stop offset="50%" stopColor="rgba(59, 130, 246, 0.05)" />
              <stop offset="100%" stopColor="rgba(168, 85, 247, 0.08)" />
            </linearGradient>
            <linearGradient id="benefits-gradient2" x1="100%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor="rgba(59, 130, 246, 0.06)" />
              <stop offset="50%" stopColor="rgba(34, 197, 94, 0.04)" />
              <stop offset="100%" stopColor="rgba(168, 85, 247, 0.06)" />
            </linearGradient>
            <pattern id="benefits-grid" width="10" height="10" patternUnits="userSpaceOnUse">
              <path d="M 10 0 L 0 0 0 10" fill="none" stroke="rgba(255,255,255,0.05)" strokeWidth="0.5"/>
            </pattern>
          </defs>
          <rect width="100" height="100" fill="url(#benefits-grid)" />
          <path 
            d="M0,15 Q30,5 60,25 T100,35 L100,100 L0,100 Z" 
            fill="url(#benefits-gradient1)"
            className="benefits-svg-path-1"
          />
          <path 
            d="M0,65 Q40,45 80,70 T100,85 L100,100 L0,100 Z" 
            fill="url(#benefits-gradient2)"
            opacity="0.8"
            className="benefits-svg-path-2"
          />
        </svg>
      </div>

<BenefitsHero/>
<BenefitsWiseCode/>



    </div>
  );
};

export default BenefitsAdmin;